import { PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';
import MapGL, {
  NavigationControl,
  MapRef,
  ViewState,
  ViewStateChangeEvent,
} from 'react-map-gl';
import maplibregl from 'maplibre-gl';
import useSupercluster from 'use-supercluster';
import 'maplibre-gl/dist/maplibre-gl.css';

import { Flex, theme } from '@chakra-ui/react';
import { ClientConfig } from 'services/clientConfig';
import { Marker } from './Markers/Marker';
import { getOutletsCenterCoordinates } from './helpers';

type PartialViewState = Pick<ViewState, 'longitude' | 'latitude' | 'zoom'>;

interface Props extends PropsWithChildren {
  outlets: AdaptedStore[];
  content: OutletsMapContent;
}

export const Map = (props: Props) => {
  const { outlets, content, children } = props;

  const initialViewState: PartialViewState = {
    longitude: 10.4515,
    latitude: 51.1657,
    zoom: 6,
  };

  const [viewState, setViewState] =
    useState<PartialViewState>(initialViewState);

  const mapRef = useRef<MapRef>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);

  const filteredOutlets = useMemo(() => {
    return outlets.filter(outlet => {
      return outlet.address.geometry?.lon && outlet.address.geometry?.lat;
    });
  }, [outlets]);

  const instructions = useMemo(() => {
    const { mapZoomInstructions, mapInstructionsMobile } = content;

    const key = '<key>';

    return {
      windows: mapZoomInstructions.replace(key, 'Ctrl'),
      mac: mapZoomInstructions.replace(key, '⌘'),
      mobile: mapInstructionsMobile,
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current || !mapContainerRef.current) return;

    const { width, height } = mapContainerRef.current.getBoundingClientRect();

    const coordinates = getOutletsCenterCoordinates({
      outlets: filteredOutlets,
      mapHeight: height,
      mapWidth: width,
    });

    if (!coordinates) return;

    mapRef.current.flyTo({
      animate: true,
      speed: 2,
      center: [coordinates.longitude, coordinates.latitude],
      zoom: coordinates.zoom,
    });
  }, [filteredOutlets]);

  const { clusters, supercluster } = useSupercluster({
    points: filteredOutlets.map(outlet => ({
      type: 'Feature',
      properties: { cluster: false, outlet },
      geometry: {
        type: 'Point',
        coordinates: [
          outlet.address.geometry?.lon || 0,
          outlet.address.geometry?.lat || 0,
        ],
      },
    })),
    // @ts-expect-error: use-supercluster typing are very bad
    bounds: mapRef.current?.getMap().getBounds().toArray().flat() || undefined,
    zoom: viewState.zoom,
    options: { radius: 75, maxZoom: 16 },
  });

  const onClickCluster = (
    clusterId: string | number | undefined,
    lat: number,
    long: number,
  ) => {
    if (clusterId === undefined) return;

    const expansionZoom = Math.min(
      supercluster?.getClusterExpansionZoom(Number(clusterId)) || 0,
      20,
    );

    mapRef.current?.flyTo({
      animate: true,
      speed: 2,
      center: [long, lat],
      zoom: expansionZoom,
    });
  };

  const onMoveMap = (event: ViewStateChangeEvent) => {
    setViewState({ ...event.viewState });
  };

  const onLoadMap = () => {
    if (!mapContainerRef.current || !mapRef.current) {
      setViewState({ ...initialViewState });

      return;
    }

    const { width, height } = mapContainerRef.current.getBoundingClientRect();

    const coordinates = getOutletsCenterCoordinates({
      outlets: filteredOutlets,
      mapHeight: height,
      mapWidth: width,
    });

    if (!coordinates) return;

    mapRef.current.flyTo({
      animate: true,
      speed: 2,
      center: [coordinates.longitude, coordinates.latitude],
      zoom: coordinates.zoom,
    });
  };

  const clientConfig = ClientConfig.getInstance().getConfig();

  return (
    <Flex
      ref={mapContainerRef}
      w="full"
      h="full"
      pos="relative"
      __css={{
        '.maplibregl-cooperative-gesture-screen': {
          zIndex: theme.zIndices.popover + 1,
        },
      }}
    >
      <MapGL
        ref={mapRef}
        // @ts-expect-error: maplibre-gl types are not up to date
        mapLib={maplibregl}
        {...viewState}
        onMove={onMoveMap}
        initialViewState={viewState}
        mapStyle={clientConfig.maptilerUrl}
        onLoad={onLoadMap}
        cooperativeGestures
        locale={{
          'CooperativeGesturesHandler.WindowsHelpText': instructions.windows,
          'CooperativeGesturesHandler.MacHelpText': instructions.mac,
          'CooperativeGesturesHandler.MobileHelpText': instructions.mobile,
        }}
      >
        {clusters.map(cluster => {
          const [longitude, latitude] = cluster.geometry.coordinates;
          const {
            cluster: isCluster,
            outlet,
            // @ts-expect-error: use-supercluster typing is very bad
            point_count: pointCount,
          } = cluster.properties;

          return (
            <Marker
              key={isCluster ? `cluster-${cluster.id}` : outlet.id}
              isCluster={isCluster}
              clusterId={cluster.id}
              latitude={latitude}
              longitude={longitude}
              onClickCluster={onClickCluster}
              pointCount={pointCount}
              outlet={outlet}
            />
          );
        })}

        <NavigationControl position="top-right" visualizePitch />

        {children}
      </MapGL>
    </Flex>
  );
};
